"use client"

import type { Dispatch, SetStateAction } from "react"

// Moved out of components/booru-detail-modal.tsx (same functions). The album state itself stays in the modal; this only
// holds the stepping rules (an ad slot is paged through like any other item).
export function useAlbumNavigation({
  viewingAdSlot,
  setViewingAdSlot,
  currentAlbumPage,
  setCurrentAlbumPage,
  totalAlbumPages,
  adBeforeRealPage,
}: {
  viewingAdSlot: number | null
  setViewingAdSlot: Dispatch<SetStateAction<number | null>>
  currentAlbumPage: number
  setCurrentAlbumPage: Dispatch<SetStateAction<number>>
  totalAlbumPages: number
  adBeforeRealPage: Map<number, number>
}) {
  const handleAlbumPrevPage = () => {
    // If an ad is currently occupying the viewer, "back" just steps off it —
    // we're already sitting on the real page it was inserted in front of.
    if (viewingAdSlot !== null) {
      setViewingAdSlot(null)
      return
    }
    if (currentAlbumPage > 0) {
      setCurrentAlbumPage((prev) => prev - 1)
    }
  }

  const handleAlbumNextPage = () => {
    // If an ad is currently shown, advancing past it reveals the real page
    // it was inserted before.
    if (viewingAdSlot !== null) {
      setViewingAdSlot(null)
      setCurrentAlbumPage((prev) => prev + 1)
      return
    }
    if (currentAlbumPage >= totalAlbumPages - 1) return
    const nextRealPage = currentAlbumPage + 1
    const adSlot = adBeforeRealPage.get(nextRealPage)
    if (adSlot !== undefined) {
      // Show the ad as its own item first — same page count either way, the
      // user just pages through it like any other item instead of it being
      // overlaid on top of real content.
      setViewingAdSlot(adSlot)
      return
    }
    setCurrentAlbumPage((prev) => prev + 1)
  }

  const handleAlbumPageClick = (pageIndex: number) => {
    setViewingAdSlot(null)
    setCurrentAlbumPage(pageIndex)
  }

  const handleAlbumAdSlotClick = (adSlot: number) => {
    setViewingAdSlot(adSlot)
  }

  return { handleAlbumPrevPage, handleAlbumNextPage, handleAlbumPageClick, handleAlbumAdSlotClick }
}
